import { useState, useEffect } from "react";
import {
  AppBar, Backdrop, Breadcrumbs, Button, Card, CardActions, CardContent,
  Link, CardMedia, CircularProgress, Container, FormControl, Grid,
  InputLabel, Select, TextField, Toolbar, Typography, Box
} from "@mui/material";
import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import DataThresholdingIcon from "@mui/icons-material/DataThresholding";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DataRef } from "../../../../types/DataRef";
import CheckAttr from "../../../../utils/CheckAttr";
import { useStyles } from "../../../forms/styles";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { downloadFile } from "../../../../utils/utils";

type EXTRACTION_TYPE = "CUMULEE" | "DETAILLEE" | "GLOBALE";

interface EXTRACTION {
  pays?: string;
  bu?: string;
  client?: string;
  date_debut: string;
  date_fin: string;
  type: EXTRACTION_TYPE;
}

const extractionTypeDatas = [
  {
    libelle: "Cumulée",
    value: "CUMULEE",
  },
  {
    libelle: "Détaillée",
    value: "DETAILLEE",
  },
  {
    libelle: "Globale",
    value: "GLOBALE",
  },
];

const initialData: EXTRACTION = {
  pays: "",
  bu: "",
  client: "",
  type: "CUMULEE",
  date_debut: "",
  date_fin: "",
};

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const RentaClient = () => {
  const classes = useStyles();

  const cookies = new Cookies();
  const token: string = cookies.get("jwt");

  const http: AxiosInstance = axios.create({
    baseURL: urlBaseApi,
    // timeout: 1000,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const [loader, setLoader] = useState<boolean>(false);
  const [openSnack, setOpenSnack] = useState<boolean>(false);
  const [errorInputDateDebut, setErrorInputDateDebut] =
    useState<boolean>(false);
  const [errorInputDateFin, setErrorInputDateFin] = useState<boolean>(false);

  const [alertMessage, setAlerteMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<AlertType>("success");

  const [dataPays, setDataPays] = useState<DataRef[]>([]);
  const [dataBusinessUnits, setDataBusinessUnits] = useState<DataRef[]>([]);
  const [dataClients, setDataClients] = useState<DataRef[]>([]);

  const [extractionDatas, setExtractionDatas] =
    useState<EXTRACTION>(initialData);

  useEffect(() => {
    http
      .get("saisie/manager/get-pays")
      .then((res) => {
        Array.isArray(res.data) && setDataPays(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const getBusinessUnitByPays = (pays: any): void => {
    setDataBusinessUnits([]);
    setDataClients([]);

    setExtractionDatas({
      ...extractionDatas,
      pays
    });

    http
      .get(`saisie/manager/get-bus/${pays}`)
      .then((res) => {
        Array.isArray(res.data) && setDataBusinessUnits(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getClientByPaysBu = (bu: any): void => {
    setDataClients([]);
    setExtractionDatas({
      ...extractionDatas,
      bu,
      client: undefined,
    });

    http
      .get(`saisie/manager/get-clients/${extractionDatas?.pays}/${bu}`)
      .then((res) => {
        Array.isArray(res.data) && setDataClients(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const setRentaGlobalView = (value: EXTRACTION_TYPE) => {
    setDataBusinessUnits([]);
    setDataClients([]);
    if (value === "GLOBALE") {
      setExtractionDatas({
        ...extractionDatas,
        type: "GLOBALE",
        pays: "",
        bu: "",
        client: "",
      });
    } else {
      setExtractionDatas({ ...initialData, type: value });
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    switch (name) {
      case "pays":
        getBusinessUnitByPays(value);
        break;
      case "bu":
        getClientByPaysBu(value);
        break;
      case "type":
        setRentaGlobalView(value);
        break;
      default:
        setExtractionDatas({
          ...extractionDatas,
          [name]: value,
        });
    }
  };

  const extract = async () => {
    setLoader(true);

    const data = {
      pays: CheckAttr(extractionDatas?.pays)
        ? extractionDatas?.pays
        : undefined,
      bu: CheckAttr(extractionDatas?.bu) ? extractionDatas?.bu : undefined,
      client: CheckAttr(extractionDatas?.client)
        ? extractionDatas?.client
        : undefined,
      type: extractionDatas?.type,
      date_debut: extractionDatas?.date_debut,
      date_fin: extractionDatas?.date_fin,
    };

    await http
      .post("extraction/renta-client", data)
      .then((res) => {
        // On télécharge le fichier
        const { sucess, filename } = res.data;
        sucess && downloadFile(filename as string);

        // setExtractionDatas(initialData);
        setAlerteMessage("Extraction efféctuée avec succès !");
        setAlertType("success");
        setOpenSnack(true);
      }).catch((error) => {
        console.log(error);
        setAlertType("error");
        setOpenSnack(true);
      }).finally(() => {
        setLoader(false);
      });
  };

  const isValidate = () => {
    let result = true;
    if (CheckAttr(extractionDatas?.date_debut) && CheckAttr(extractionDatas?.date_fin)) {
      const dateDeb = new Date(extractionDatas?.date_debut);
      const dateFin = new Date(extractionDatas?.date_fin);

      if (dateDeb.getTime() > dateFin.getTime()) {
        setErrorInputDateDebut(true);
        setErrorInputDateFin(true);

        setAlerteMessage("Désolé! La date fin doit être supèrieur ou égal à la date debut !");
        setAlertType("warning");
        setOpenSnack(true);

        result = false;
      }
    } else {
      !CheckAttr(extractionDatas?.date_debut) && setErrorInputDateDebut(true);
      !CheckAttr(extractionDatas?.date_fin) && setErrorInputDateFin(true);

      setAlerteMessage("Merci de remplir la date debut et date fin !");
      setAlertType("warning");
      setOpenSnack(true);

      result = false;
    }

    return result;
  };

  const handleExtract = () => {
    setErrorInputDateDebut(false);
    setErrorInputDateFin(false);

    // Fait l'extraction
    isValidate() && extract();
  };

  const handleCloseAlert = () => {
    setOpenSnack(false);
  };

  const stackAlert: JSX.Element = SnackBarAlert(
    openSnack,
    handleCloseAlert,
    alertType,
    alertMessage
  );

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#FC8404",
          mb: 10,
        }}
      >
        <Toolbar disableGutters>
          <Breadcrumbs aria-label="breadcrumb" sx={{ color: "#fff", ml: 2 }}>
            <Link
              underline="hover"
              sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
              color="inherit"
              href="#"
            >
              <ChromeReaderModeIcon sx={{ mr: 1 }} fontSize="medium" />
              Extraction
            </Link>
            <Typography sx={{ display: "flex", alignItems: "center" }}>
              <DataThresholdingIcon sx={{ mr: 1 }} fontSize="medium" />
              Renta clients
            </Typography>
          </Breadcrumbs>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md">
        <Card sx={{ maxWidth: 845 }}>
          <CardMedia
            component="img"
            image="/images/extraction_renta_client.png"
            alt="green iguana"
            sx={{ height: 140 }}
          />
          <CardContent>
            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
              <Grid item xs={6} sm={6} md={6}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  sx={{ marginLeft: 1, marginTop: 1 }}
                  fullWidth
                >
                  <InputLabel
                    htmlFor="pays"
                    color="warning"
                    shrink={CheckAttr(extractionDatas?.pays)}
                  >
                    Pays de production
                  </InputLabel>
                  <Select
                    id="pays"
                    native
                    color="warning"
                    value={extractionDatas?.pays}
                    disabled={extractionDatas?.type === "GLOBALE"}
                    onChange={handleChange}
                    label="Pays de production"
                    inputProps={{
                      name: "pays",
                    }}
                  >
                    <option value=""></option>
                    {dataPays.map((pays) => (
                      <option key={pays?.id} value={String(pays?.id)}>
                        {pays?.libelle}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  sx={{ marginLeft: 1, marginTop: 1 }}
                  fullWidth
                >
                  <InputLabel
                    htmlFor="client"
                    color="warning"
                    shrink={CheckAttr(extractionDatas?.client)}
                  >
                    Client
                  </InputLabel>
                  <Select
                    id="client"
                    native
                    color="warning"
                    value={extractionDatas?.client}
                    onChange={handleChange}
                    disabled={extractionDatas?.type === "GLOBALE"}
                    label="Client"
                    inputProps={{
                      name: "client",
                    }}
                  >
                    <option value=""></option>
                    {dataClients.map((client) => (
                      <option key={client?.id} value={client?.id}>
                        {client?.libelle}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  sx={{ marginLeft: 1, marginTop: 1 }}
                  fullWidth
                >
                  <InputLabel
                    htmlFor="bu"
                    color="warning"
                    shrink={CheckAttr(extractionDatas?.bu)}
                  >
                    Business unit
                  </InputLabel>
                  <Select
                    id="bu"
                    native
                    color="warning"
                    value={extractionDatas?.bu}
                    onChange={handleChange}
                    disabled={extractionDatas?.type === "GLOBALE"}
                    label="Business unit"
                    inputProps={{
                      name: "bu",
                    }}
                  >
                    <option value=""></option>
                    {dataBusinessUnits.map((bu) => (
                      <option key={bu?.id} value={bu?.id}>
                        {bu?.libelle}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
              <Grid item xs={6} sm={6} md={6}>
                <TextField
                  name="date_debut"
                  label="Date debut"
                  type="date"
                  color="warning"
                  fullWidth
                  error={errorInputDateDebut}
                  value={extractionDatas?.date_debut}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ mt: 2, ml: 1 }}
                />

                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  sx={{ marginLeft: 1, marginTop: 5 }}
                  fullWidth
                >
                  <InputLabel
                    htmlFor="type"
                    color="warning"
                    shrink={CheckAttr(extractionDatas?.type)}
                  >
                    Type d'extraction
                  </InputLabel>
                  <Select
                    id="type"
                    native
                    color="warning"
                    value={extractionDatas?.type}
                    onChange={handleChange}
                    label="Type d'extraction"
                    inputProps={{
                      name: "type",
                    }}
                  >
                    {extractionTypeDatas.map((item, index) => (
                      <option key={index} value={item.value}>
                        {item.libelle}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <TextField
                  name="date_fin"
                  label="Date fin"
                  type="date"
                  color="warning"
                  fullWidth
                  error={errorInputDateFin}
                  value={extractionDatas?.date_fin}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ mt: 2, ml: 1 }}
                />
              </Grid>
            </Grid>
          </CardContent>

          <CardActions>
            <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid item xs={12} sm={12} md={12}>
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    color="warning"
                    size="large"
                    sx={{ textTransform: "none" }}
                    endIcon={<FileDownloadIcon />}
                    onClick={() => handleExtract()}
                  >
                    Extraire
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </Container>
      {stackAlert}
      <div>
        <Backdrop
          sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </>
  );
};

export default RentaClient;
